import { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Stack,
  Autocomplete,
  Chip,
  Badge,
} from '@mui/material';
import {
  Save as SaveIcon,
  Business as FacilityIcon,
  ContactPhone as ContactIcon,
  EmailOutlined,
  Add as AddIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { PageWrapper } from 'src/components/PageWrapper';
import { useGetAllFacultiesQuery, useUpdateFacultyMutation } from 'src/services/restApi/queries/faculty.service';
import {
  useGetAllContactsQuery,
  useGetUnassignedContactsQuery,
  useUpdateContactsMutation,
} from 'src/services/restApi/queries/contact.service';
import { useGetPendingInvitationsQuery, useGetLoggedInUserQuery } from 'src/services/restApi/queries/users.service';
import { useDialogStore } from 'src/store/dialog/dialogStore';
import { CreateContactModal } from './components/createContactModal';
import { CreateFacultyModal } from './components/createFacultyModal';
import { useAuth } from 'src/features/authentication';
import { UserManagement } from '../userManagement/UserManagement';
import { PendingInviteList } from '../userManagement/components/PendingInviteList';
import { InviteUserModal } from '../userManagement/components/InviteUserModal';

const validationSchemas = {
  faculty: z.object({
    id: z.string().min(1, 'Faculty ID is required'),
    name: z.string().min(3, 'Faculty name is required'),
    contactId: z.string().min(1, 'Select a contact'),
  }),
  contact: z.object({
    id: z.string().min(1, 'Contact ID is required'),
    contactName: z.string().min(1, 'Contact name is required'),
    contactEmail: z.string().email('Invalid email'),
  }),
};

type FacultyFormData = z.infer<typeof validationSchemas.faculty>;
type ContactFormData = z.infer<typeof validationSchemas.contact>;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`management-tabpanel-${index}`}
      aria-labelledby={`management-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ManagementSettings: React.FC = () => {
  const {
    data: fetchedFaculties,
    refetch: refetchFaculties,
    isLoading: isLoadingFaculties,
  } = useGetAllFacultiesQuery();
  const { data: fetchedContacts, refetch: refetchContacts, isLoading: isLoadingContacts } = useGetAllContactsQuery();
  const {
    data: fetchedUnassignedContacts,
    refetch: refetchUnassigned,
    isLoading: isLoadingUnassigned,
  } = useGetUnassignedContactsQuery();
  const [updateFaculty] = useUpdateFacultyMutation();
  const [updateContact] = useUpdateContactsMutation();

  const { user: authUser } = useAuth();
  const { data: currentUser } = useGetLoggedInUserQuery(authUser?.uid ?? '', {
    skip: !authUser?.uid,
  });
  const currentUserRole = currentUser?.role ?? 'USER';

  const { data: pendingInvites } = useGetPendingInvitationsQuery();
  const inviteCount = pendingInvites?.length ?? 0;

  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const { closeDialog, openDialog } = useDialogStore((state) => state);
  const [selectedTab, setSelectedTab] = useState(0);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [open, setOpen] = useState(false);

  // Faculty form
  const facultyForm = useForm<FacultyFormData>({
    resolver: zodResolver(validationSchemas.faculty),
    defaultValues: { id: '', name: '', contactId: '' },
    mode: 'onChange',
  });

  // Contact form
  const contactForm = useForm<ContactFormData>({
    resolver: zodResolver(validationSchemas.contact),
    defaultValues: { id: '', contactName: '', contactEmail: '' },
    mode: 'onChange',
  });

  const {
    control: facultyControl,
    handleSubmit: facultyHandleSubmit,
    formState: { errors: facultyErrors, isValid: facultyIsValid, isDirty: facultyIsDirty },
    reset: facultyReset,
    watch: facultyWatch,
  } = facultyForm;

  const {
    control: contactControl,
    handleSubmit: contactHandleSubmit,
    formState: { errors: contactErrors, isValid: contactIsValid, isDirty: contactIsDirty },
    reset: contactReset,
    watch: contactWatch,
  } = contactForm;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => setSelectedTab(newValue);

  const showMessage = (text: string, type: 'success' | 'error') => {
    setMessage({ text, type });
    setOpen(true);
  };

  const onFacultySubmit = async (values: FacultyFormData) => {
    try {
      await updateFaculty({
        id: values.id,
        name: values.name,
        contactId: values.contactId,
      });
      showMessage('Faculty updated successfully!', 'success');

      const refreshed = await refetchFaculties().unwrap();

      // Reset form with updated data
      const updatedFaculty = refreshed?.find((f) => f.id === values.id);
      facultyReset({
        id: values.id,
        name: updatedFaculty?.name || '',
      });
      refetchUnassigned();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update faculty.';
      showMessage(errorMessage, 'error');
    }
  };

  const onContactSubmit = async (values: ContactFormData) => {
    try {
      await updateContact({
        id: values.id,
        name: values.contactName,
        email: values.contactEmail,
      });

      showMessage('Contact updated successfully!', 'success');

      const refreshed = await refetchContacts().unwrap();

      const updatedContact = refreshed.find((c) => c.id === values.id);

      contactReset({
        id: values.id,
        contactName: updatedContact?.name || '',
        contactEmail: updatedContact?.email || '',
      });

      await refetchUnassigned();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update contact.';
      showMessage(errorMessage, 'error');
    }
  };

  const breadcrumbs = [{ label: 'Settings', href: '/settings' }];

  const actions = (
    <Stack direction='row' spacing={1}>
      {selectedTab !== 2 && selectedTab !== 3 && (
        <Button
          variant='outlined'
          startIcon={<AddIcon />}
          onClick={() => {
            if (selectedTab === 0) {
              openDialog({
                dialogContent: <CreateFacultyModal />,
              });
            } else {
              openDialog({
                dialogContent: <CreateContactModal />,
              });
            }
          }}
        >
          {selectedTab === 0 ? 'Add Faculty' : 'Add Contact'}
        </Button>
      )}
    </Stack>
  );

  return (
    <PageWrapper
      title='Management Settings'
      subtitle='Manage facilities and contacts for the organization'
      breadcrumbs={breadcrumbs}
      action={actions}
    >
      <Snackbar
        open={open}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={5000}
        onClose={() => setOpen(false)}
      >
        <Alert onClose={() => setOpen(false)} severity={message?.type} sx={{ width: '100%' }}>
          {message?.text}
        </Alert>
      </Snackbar>

      <Card elevation={0} variant='outlined' sx={{ boxShadow: 'none' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={selectedTab} onChange={handleTabChange} aria-label='management settings tabs'>
            <Tab
              icon={<FacilityIcon />}
              iconPosition='start'
              label='Manage Facilities'
              id='management-tab-0'
              aria-controls='management-tabpanel-0'
            />
            <Tab
              icon={<ContactIcon />}
              iconPosition='start'
              label='Manage Contacts'
              id='management-tab-1'
              aria-controls='management-tabpanel-1'
            />
            <Tab
              icon={<PeopleIcon />}
              iconPosition='start'
              label='Manage Users'
              id='management-tab-2'
              aria-controls='management-tabpanel-2'
            />
            <Tab
              icon={<EmailOutlined />}
              iconPosition='start'
              label={
                <Badge badgeContent={inviteCount} color='warning' max={99}>
                  <Box sx={{ pr: inviteCount > 0 ? 1.5 : 0 }}>Invite Management</Box>
                </Badge>
              }
              id='management-tab-3'
              aria-controls='management-tabpanel-3'
            />
          </Tabs>
        </Box>

        {/* Faculty Management Tab */}
        <TabPanel value={selectedTab} index={0}>
          <CardContent sx={{ pt: 0 }}>
            <Stack spacing={2}>
              {/* Faculty Selection with Autocomplete */}
              <Controller
                name='id'
                control={facultyControl}
                render={({ field }) => (
                  <Autocomplete
                    options={fetchedFaculties || []}
                    getOptionLabel={(option) => option.name ?? ''}
                    value={fetchedFaculties?.find((f) => f.id === field.value) || null}
                    onChange={(_event, newValue) => {
                      const facultyId = newValue?.id || '';
                      const faculty = fetchedFaculties?.find((f) => f.id === facultyId);
                      facultyReset({
                        id: facultyId,
                        name: facultyId ? faculty?.name || '' : '',
                        contactId: facultyId ? faculty?.contactId || '' : '',
                      });
                    }}
                    loading={isLoadingFaculties}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Select Faculty to Edit'
                        placeholder='Type to search faculties...'
                        fullWidth
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component='li' {...props} key={option.id}>
                        <Stack direction='column' spacing={0.5} sx={{ width: '100%' }}>
                          <Typography variant='body1' fontWeight={600}>
                            {option.name}
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            Contact: {option.contact?.name || 'No Contact Assigned'}
                            {option.contact?.email && ` (${option.contact.email})`}
                          </Typography>
                        </Stack>
                      </Box>
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    clearOnEscape
                    fullWidth
                  />
                )}
              />

              {/* Faculty Edit Form */}
              {facultyWatch('id') && (
                <Card variant='outlined' sx={{ boxShadow: 'none', pt: 1 }}>
                  <CardContent>
                    <form onSubmit={facultyHandleSubmit(onFacultySubmit)} noValidate>
                      <Stack spacing={2}>
                        {/* Current Contact Display */}
                        {(() => {
                          const currentFaculty = fetchedFaculties?.find((f) => f.id === facultyWatch('id'));
                          const currentContact = currentFaculty?.contact;

                          return (
                            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant='subtitle1' color='text.secondary' gutterBottom>
                                Currently Assigned Contact
                              </Typography>
                              {currentContact ? (
                                <Stack spacing={0.5}>
                                  <Typography variant='body1' fontWeight={600}>
                                    {currentContact.name}
                                  </Typography>
                                  <Typography variant='body2' color='text.secondary'>
                                    <EmailOutlined fontSize='small' sx={{ verticalAlign: 'middle', mr: 0.75 }} />
                                    {currentContact.email}
                                  </Typography>
                                </Stack>
                              ) : (
                                <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic' }}>
                                  No contact currently assigned
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}
                        <Controller
                          name='name'
                          control={facultyControl}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label='Faculty Name'
                              fullWidth
                              error={!!facultyErrors.name}
                              helperText={facultyErrors.name?.message}
                            />
                          )}
                        />

                        <Box>
                          <Controller
                            name='contactId'
                            control={facultyControl}
                            render={({ field }) => (
                              <FormControl fullWidth error={!!facultyErrors.contactId}>
                                <InputLabel>Select New Contact</InputLabel>
                                <Select {...field} label='Select New Contact'>
                                  {/* Available unassigned contacts */}
                                  {fetchedUnassignedContacts &&
                                    fetchedUnassignedContacts.length > 0 &&
                                    fetchedUnassignedContacts.map((contact) => (
                                      <MenuItem key={contact.id} value={contact.id}>
                                        <Stack>
                                          <Typography variant='body2' fontWeight={600}>
                                            {contact.name}
                                          </Typography>
                                          <Typography variant='caption' color='text.secondary'>
                                            {contact.email}
                                          </Typography>
                                        </Stack>
                                      </MenuItem>
                                    ))}
                                </Select>
                                {facultyErrors.contactId && (
                                  <Typography variant='caption' color='error' sx={{ mt: 0.5, ml: 1.75 }}>
                                    {facultyErrors.contactId.message}
                                  </Typography>
                                )}
                              </FormControl>
                            )}
                          />

                          {(!fetchedUnassignedContacts || fetchedUnassignedContacts.length === 0) && (
                            <Typography variant='body2' color='text.secondary' sx={{ mt: 1, fontStyle: 'italic' }}>
                              No unassigned contacts available. All contacts are currently assigned to faculties.
                            </Typography>
                          )}
                        </Box>

                        <Stack direction='row' spacing={1} justifyContent='flex-end'>
                          <Button
                            variant='outlined'
                            onClick={() => {
                              facultyReset({
                                id: '',
                                name: '',
                                contactId: '',
                              });
                            }}
                          >
                            Cancel
                          </Button>

                          <Button
                            type='submit'
                            variant='contained'
                            disabled={!facultyIsValid || !facultyIsDirty}
                            startIcon={<SaveIcon />}
                          >
                            Save Changes
                          </Button>
                        </Stack>
                      </Stack>
                    </form>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </CardContent>
        </TabPanel>

        {/* Contact Management Tab */}
        <TabPanel value={selectedTab} index={1}>
          <CardContent sx={{ pt: 0 }}>
            <Stack spacing={2}>
              {/* Contact Selection with Autocomplete */}
              <Controller
                name='id'
                control={contactControl}
                render={({ field }) => (
                  <Autocomplete
                    options={fetchedContacts || []}
                    getOptionLabel={(option) => option.name ?? ''}
                    value={fetchedContacts?.find((c) => c.id === field.value) || null}
                    loading={isLoadingContacts}
                    onChange={(_event, newValue) => {
                      const contactId = newValue?.id || '';
                      contactReset({
                        id: contactId,
                        contactName: contactId ? fetchedContacts?.find((c) => c.id === contactId)?.name || '' : '',
                        contactEmail: contactId ? fetchedContacts?.find((c) => c.id === contactId)?.email || '' : '',
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Select Contact to Edit'
                        placeholder='Type to search contacts...'
                        fullWidth
                      />
                    )}
                    renderOption={(props, option) => {
                      // Check if this contact is assigned to any faculty
                      const assignedFaculty = fetchedFaculties?.find((f) => f.contactId === option.id);

                      return (
                        <Box component='li' {...props} key={option.id}>
                          <Stack direction='column' spacing={0.5} sx={{ width: '100%' }}>
                            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                              <Typography variant='body1' fontWeight={600}>
                                {option.name}
                              </Typography>
                              {assignedFaculty && (
                                <Chip label='Assigned' size='small' color='primary' variant='outlined' />
                              )}
                            </Stack>
                            <Typography variant='body2' color='text.secondary'>
                              {option.email}
                            </Typography>
                            {assignedFaculty && (
                              <Typography variant='caption' color='text.secondary'>
                                Assigned to: {assignedFaculty.name}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      );
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    clearOnEscape
                    fullWidth
                  />
                )}
              />

              {/* Contact Edit Form */}
              {contactWatch('id') && (
                <Card variant='outlined' sx={{ boxShadow: 'none', pt: 1 }}>
                  <CardContent>
                    <form onSubmit={contactHandleSubmit(onContactSubmit)}>
                      <Stack spacing={2}>
                        {/* Current Assignment Display */}
                        {(() => {
                          const assignedFaculty = fetchedFaculties?.find((f) => f.contactId === contactWatch('id'));

                          return (
                            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant='subtitle1' color='text.secondary' gutterBottom>
                                Contact Assignment Status
                              </Typography>
                              {assignedFaculty ? (
                                <Typography variant='body2' fontWeight={600}>
                                  Assigned to: {assignedFaculty.name}
                                </Typography>
                              ) : (
                                <Typography variant='body2' color='text.secondary'>
                                  Not assigned to any faculty
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}
                        <Controller
                          name='contactName'
                          control={contactControl}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label='Contact Name'
                              fullWidth
                              error={!!contactErrors.contactName}
                              helperText={contactErrors.contactName?.message}
                            />
                          )}
                        />

                        <Controller
                          name='contactEmail'
                          control={contactControl}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label='Contact Email'
                              type='email'
                              fullWidth
                              error={!!contactErrors.contactEmail}
                              helperText={contactErrors.contactEmail?.message}
                            />
                          )}
                        />

                        <Stack direction='row' spacing={1} justifyContent='flex-end'>
                          <Button
                            variant='outlined'
                            onClick={() => {
                              contactReset({
                                id: '',
                                contactName: '',
                                contactEmail: '',
                              });
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            type='submit'
                            variant='contained'
                            disabled={!contactIsValid || !contactIsDirty}
                            startIcon={<SaveIcon />}
                          >
                            Save Changes
                          </Button>
                        </Stack>
                      </Stack>
                    </form>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </CardContent>
        </TabPanel>

        {/* User Management Tab */}
        <TabPanel value={selectedTab} index={2}>
          <CardContent sx={{ pt: 0 }}>
            <UserManagement />
          </CardContent>
        </TabPanel>

        {/* Invite Management Tab */}
        <TabPanel value={selectedTab} index={3}>
          <CardContent sx={{ pt: 0 }}>
            <Box display='flex' justifyContent='flex-end' mb={2}>
              <Button variant='contained' startIcon={<AddIcon />} onClick={() => setInviteModalOpen(true)}>
                Invite User
              </Button>
            </Box>
            <PendingInviteList />
            <InviteUserModal
              open={inviteModalOpen}
              onClose={() => setInviteModalOpen(false)}
              currentUserRole={currentUserRole}
            />
          </CardContent>
        </TabPanel>
      </Card>
    </PageWrapper>
  );
};

export default ManagementSettings;
